/**
* @license Apache-2.0
*
* Copyright (c) 2021 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name AccessorArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/accessor}
*/
import AccessorArray from '@stdlib/array-base-accessor';
setReadOnly( ns, 'AccessorArray', AccessorArray );

/**
* @name accessorGetter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/accessor-getter}
*/
import accessorGetter from '@stdlib/array-base-accessor-getter';
setReadOnly( ns, 'accessorGetter', accessorGetter );

/**
* @name accessorSetter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/accessor-setter}
*/
import accessorSetter from '@stdlib/array-base-accessor-setter';
setReadOnly( ns, 'accessorSetter', accessorSetter );

/**
* @name accessors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/accessors}
*/
import accessors from '@stdlib/array-base-accessors';
setReadOnly( ns, 'accessors', accessors );

/**
* @name any
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any}
*/
import any from '@stdlib/array-base-any';
setReadOnly( ns, 'any', any );

/**
* @name anyBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-by}
*/
import anyBy from '@stdlib/array-base-any-by';
setReadOnly( ns, 'anyBy', anyBy );

/**
* @name anyByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-by-right}
*/
import anyByRight from '@stdlib/array-base-any-by-right';
setReadOnly( ns, 'anyByRight', anyByRight );

/**
* @name anyHasOwnProp
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-has-own-property}
*/
import anyHasOwnProp from '@stdlib/array-base-any-has-own-property';
setReadOnly( ns, 'anyHasOwnProp', anyHasOwnProp );

/**
* @name anyHasProp
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-has-property}
*/
import anyHasProp from '@stdlib/array-base-any-has-property';
setReadOnly( ns, 'anyHasProp', anyHasProp );

/**
* @name anyIsEntry
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-is-entry}
*/
import anyIsEntry from '@stdlib/array-base-any-is-entry';
setReadOnly( ns, 'anyIsEntry', anyIsEntry );

/**
* @name anyIsEntryIn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/any-is-entry-in}
*/
import anyIsEntryIn from '@stdlib/array-base-any-is-entry-in';
setReadOnly( ns, 'anyIsEntryIn', anyIsEntryIn );

/**
* @name arraylike2object
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/arraylike2object}
*/
import arraylike2object from '@stdlib/array-base-arraylike2object';
setReadOnly( ns, 'arraylike2object', arraylike2object );

/**
* @name assert
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/assert}
*/
import assert from '@stdlib/array-base-assert';
setReadOnly( ns, 'assert', assert );

/**
* @name at
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/at}
*/
import at from '@stdlib/array-base-at';
setReadOnly( ns, 'at', at );

/**
* @name at2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/at2d}
*/
import at2d from '@stdlib/array-base-at2d';
setReadOnly( ns, 'at2d', at2d );

/**
* @name at3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/at3d}
*/
import at3d from '@stdlib/array-base-at3d';
setReadOnly( ns, 'at3d', at3d );

/**
* @name at4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/at4d}
*/
import at4d from '@stdlib/array-base-at4d';
setReadOnly( ns, 'at4d', at4d );

/**
* @name at5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/at5d}
*/
import at5d from '@stdlib/array-base-at5d';
setReadOnly( ns, 'at5d', at5d );

/**
* @name atnd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/atnd}
*/
import atnd from '@stdlib/array-base-atnd';
setReadOnly( ns, 'atnd', atnd );

/**
* @name banded
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/banded}
*/
import banded from '@stdlib/array-base-banded';
setReadOnly( ns, 'banded', banded );

/**
* @name bifurcateEntries
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-entries}
*/
import bifurcateEntries from '@stdlib/array-base-bifurcate-entries';
setReadOnly( ns, 'bifurcateEntries', bifurcateEntries );

/**
* @name bifurcateEntriesBy
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-entries-by}
*/
import bifurcateEntriesBy from '@stdlib/array-base-bifurcate-entries-by';
setReadOnly( ns, 'bifurcateEntriesBy', bifurcateEntriesBy );

/**
* @name bifurcateIndices
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-indices}
*/
import bifurcateIndices from '@stdlib/array-base-bifurcate-indices';
setReadOnly( ns, 'bifurcateIndices', bifurcateIndices );

/**
* @name bifurcateIndicesBy
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-indices-by}
*/
import bifurcateIndicesBy from '@stdlib/array-base-bifurcate-indices-by';
setReadOnly( ns, 'bifurcateIndicesBy', bifurcateIndicesBy );

/**
* @name bifurcateValues
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-values}
*/
import bifurcateValues from '@stdlib/array-base-bifurcate-values';
setReadOnly( ns, 'bifurcateValues', bifurcateValues );

/**
* @name bifurcateValuesBy
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/bifurcate-values-by}
*/
import bifurcateValuesBy from '@stdlib/array-base-bifurcate-values-by';
setReadOnly( ns, 'bifurcateValuesBy', bifurcateValuesBy );

/**
* @name binary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/binary2d}
*/
import binary2d from '@stdlib/array-base-binary2d';
setReadOnly( ns, 'binary2d', binary2d );

/**
* @name binary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/binary3d}
*/
import binary3d from '@stdlib/array-base-binary3d';
setReadOnly( ns, 'binary3d', binary3d );

/**
* @name binary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/binary4d}
*/
import binary4d from '@stdlib/array-base-binary4d';
setReadOnly( ns, 'binary4d', binary4d );

/**
* @name binary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/binary5d}
*/
import binary5d from '@stdlib/array-base-binary5d';
setReadOnly( ns, 'binary5d', binary5d );

/**
* @name binarynd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/binarynd}
*/
import binarynd from '@stdlib/array-base-binarynd';
setReadOnly( ns, 'binarynd', binarynd );

/**
* @name broadcastArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcast-array}
*/
import broadcastArray from '@stdlib/array-base-broadcast-array';
setReadOnly( ns, 'broadcastArray', broadcastArray );

/**
* @name bbinary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-binary2d}
*/
import bbinary2d from '@stdlib/array-base-broadcasted-binary2d';
setReadOnly( ns, 'bbinary2d', bbinary2d );

/**
* @name bbinary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-binary3d}
*/
import bbinary3d from '@stdlib/array-base-broadcasted-binary3d';
setReadOnly( ns, 'bbinary3d', bbinary3d );

/**
* @name bbinary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-binary4d}
*/
import bbinary4d from '@stdlib/array-base-broadcasted-binary4d';
setReadOnly( ns, 'bbinary4d', bbinary4d );

/**
* @name bbinary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-binary5d}
*/
import bbinary5d from '@stdlib/array-base-broadcasted-binary5d';
setReadOnly( ns, 'bbinary5d', bbinary5d );

/**
* @name bquaternary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quaternary2d}
*/
import bquaternary2d from '@stdlib/array-base-broadcasted-quaternary2d';
setReadOnly( ns, 'bquaternary2d', bquaternary2d );

/**
* @name bquaternary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quaternary3d}
*/
import bquaternary3d from '@stdlib/array-base-broadcasted-quaternary3d';
setReadOnly( ns, 'bquaternary3d', bquaternary3d );

/**
* @name bquaternary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quaternary4d}
*/
import bquaternary4d from '@stdlib/array-base-broadcasted-quaternary4d';
setReadOnly( ns, 'bquaternary4d', bquaternary4d );

/**
* @name bquaternary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quaternary5d}
*/
import bquaternary5d from '@stdlib/array-base-broadcasted-quaternary5d';
setReadOnly( ns, 'bquaternary5d', bquaternary5d );

/**
* @name bquinary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quinary2d}
*/
import bquinary2d from '@stdlib/array-base-broadcasted-quinary2d';
setReadOnly( ns, 'bquinary2d', bquinary2d );

/**
* @name bquinary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-quinary4d}
*/
import bquinary4d from '@stdlib/array-base-broadcasted-quinary4d';
setReadOnly( ns, 'bquinary4d', bquinary4d );

/**
* @name bternary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-ternary2d}
*/
import bternary2d from '@stdlib/array-base-broadcasted-ternary2d';
setReadOnly( ns, 'bternary2d', bternary2d );

/**
* @name bternary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-ternary3d}
*/
import bternary3d from '@stdlib/array-base-broadcasted-ternary3d';
setReadOnly( ns, 'bternary3d', bternary3d );

/**
* @name bternary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-ternary4d}
*/
import bternary4d from '@stdlib/array-base-broadcasted-ternary4d';
setReadOnly( ns, 'bternary4d', bternary4d );

/**
* @name bternary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-ternary5d}
*/
import bternary5d from '@stdlib/array-base-broadcasted-ternary5d';
setReadOnly( ns, 'bternary5d', bternary5d );

/**
* @name bunary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-unary2d}
*/
import bunary2d from '@stdlib/array-base-broadcasted-unary2d';
setReadOnly( ns, 'bunary2d', bunary2d );

/**
* @name bunary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-unary3d}
*/
import bunary3d from '@stdlib/array-base-broadcasted-unary3d';
setReadOnly( ns, 'bunary3d', bunary3d );

/**
* @name bunary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-unary4d}
*/
import bunary4d from '@stdlib/array-base-broadcasted-unary4d';
setReadOnly( ns, 'bunary4d', bunary4d );

/**
* @name bunary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/broadcasted-unary5d}
*/
import bunary5d from '@stdlib/array-base-broadcasted-unary5d';
setReadOnly( ns, 'bunary5d', bunary5d );

/**
* @name cartesianPower
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cartesian-power}
*/
import cartesianPower from '@stdlib/array-base-cartesian-power';
setReadOnly( ns, 'cartesianPower', cartesianPower );

/**
* @name cartesianProduct
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cartesian-product}
*/
import cartesianProduct from '@stdlib/array-base-cartesian-product';
setReadOnly( ns, 'cartesianProduct', cartesianProduct );

/**
* @name cartesianSquare
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cartesian-square}
*/
import cartesianSquare from '@stdlib/array-base-cartesian-square';
setReadOnly( ns, 'cartesianSquare', cartesianSquare );

/**
* @name copy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/copy}
*/
import copy from '@stdlib/array-base-copy';
setReadOnly( ns, 'copy', copy );

/**
* @name copyIndexed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/copy-indexed}
*/
import copyIndexed from '@stdlib/array-base-copy-indexed';
setReadOnly( ns, 'copyIndexed', copyIndexed );

/**
* @name countFalsy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-falsy}
*/
import countFalsy from '@stdlib/array-base-count-falsy';
setReadOnly( ns, 'countFalsy', countFalsy );

/**
* @name countIf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-if}
*/
import countIf from '@stdlib/array-base-count-if';
setReadOnly( ns, 'countIf', countIf );

/**
* @name countIfs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-ifs}
*/
import countIfs from '@stdlib/array-base-count-ifs';
setReadOnly( ns, 'countIfs', countIfs );

/**
* @name countSameValue
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-same-value}
*/
import countSameValue from '@stdlib/array-base-count-same-value';
setReadOnly( ns, 'countSameValue', countSameValue );

/**
* @name countSameValueZero
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-same-value-zero}
*/
import countSameValueZero from '@stdlib/array-base-count-same-value-zero';
setReadOnly( ns, 'countSameValueZero', countSameValueZero );

/**
* @name countTruthy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/count-truthy}
*/
import countTruthy from '@stdlib/array-base-count-truthy';
setReadOnly( ns, 'countTruthy', countTruthy );

/**
* @name cuany
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuany}
*/
import cuany from '@stdlib/array-base-cuany';
setReadOnly( ns, 'cuany', cuany );

/**
* @name cuanyBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuany-by}
*/
import cuanyBy from '@stdlib/array-base-cuany-by';
setReadOnly( ns, 'cuanyBy', cuanyBy );

/**
* @name cuanyByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuany-by-right}
*/
import cuanyByRight from '@stdlib/array-base-cuany-by-right';
setReadOnly( ns, 'cuanyByRight', cuanyByRight );

/**
* @name cuevery
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuevery}
*/
import cuevery from '@stdlib/array-base-cuevery';
setReadOnly( ns, 'cuevery', cuevery );

/**
* @name cueveryBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuevery-by}
*/
import cueveryBy from '@stdlib/array-base-cuevery-by';
setReadOnly( ns, 'cueveryBy', cueveryBy );

/**
* @name cueveryByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cuevery-by-right}
*/
import cueveryByRight from '@stdlib/array-base-cuevery-by-right';
setReadOnly( ns, 'cueveryByRight', cueveryByRight );

/**
* @name cunone
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cunone}
*/
import cunone from '@stdlib/array-base-cunone';
setReadOnly( ns, 'cunone', cunone );

/**
* @name cunoneBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cunone-by}
*/
import cunoneBy from '@stdlib/array-base-cunone-by';
setReadOnly( ns, 'cunoneBy', cunoneBy );

/**
* @name cunoneByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cunone-by-right}
*/
import cunoneByRight from '@stdlib/array-base-cunone-by-right';
setReadOnly( ns, 'cunoneByRight', cunoneByRight );

/**
* @name cusome
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cusome}
*/
import cusome from '@stdlib/array-base-cusome';
setReadOnly( ns, 'cusome', cusome );

/**
* @name cusomeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cusome-by}
*/
import cusomeBy from '@stdlib/array-base-cusome-by';
setReadOnly( ns, 'cusomeBy', cusomeBy );

/**
* @name cusomeByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/cusome-by-right}
*/
import cusomeByRight from '@stdlib/array-base-cusome-by-right';
setReadOnly( ns, 'cusomeByRight', cusomeByRight );

/**
* @name dedupe
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/dedupe}
*/
import dedupe from '@stdlib/array-base-dedupe';
setReadOnly( ns, 'dedupe', dedupe );

/**
* @name entries2objects
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/entries2objects}
*/
import entries2objects from '@stdlib/array-base-entries2objects';
setReadOnly( ns, 'entries2objects', entries2objects );

/**
* @name entries2views
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/entries2views}
*/
import entries2views from '@stdlib/array-base-entries2views';
setReadOnly( ns, 'entries2views', entries2views );

/**
* @name every
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/every}
*/
import every from '@stdlib/array-base-every';
setReadOnly( ns, 'every', every );

/**
* @name everyBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/every-by}
*/
import everyBy from '@stdlib/array-base-every-by';
setReadOnly( ns, 'everyBy', everyBy );

/**
* @name everyByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/every-by-right}
*/
import everyByRight from '@stdlib/array-base-every-by-right';
setReadOnly( ns, 'everyByRight', everyByRight );

/**
* @name falses
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/falses}
*/
import falses from '@stdlib/array-base-falses';
setReadOnly( ns, 'falses', falses );

/**
* @name fancySlice
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fancy-slice}
*/
import fancySlice from '@stdlib/array-base-fancy-slice';
setReadOnly( ns, 'fancySlice', fancySlice );

/**
* @name fancySliceAssign
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fancy-slice-assign}
*/
import fancySliceAssign from '@stdlib/array-base-fancy-slice-assign';
setReadOnly( ns, 'fancySliceAssign', fancySliceAssign );

/**
* @name fill
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fill}
*/
import fill from '@stdlib/array-base-fill';
setReadOnly( ns, 'fill', fill );

/**
* @name fillBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fill-by}
*/
import fillBy from '@stdlib/array-base-fill-by';
setReadOnly( ns, 'fillBy', fillBy );

/**
* @name filled
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled}
*/
import filled from '@stdlib/array-base-filled';
setReadOnly( ns, 'filled', filled );

/**
* @name filledBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled-by}
*/
import filledBy from '@stdlib/array-base-filled-by';
setReadOnly( ns, 'filledBy', filledBy );

/**
* @name filled2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled2d}
*/
import filled2d from '@stdlib/array-base-filled2d';
setReadOnly( ns, 'filled2d', filled2d );

/**
* @name filled2dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled2d-by}
*/
import filled2dBy from '@stdlib/array-base-filled2d-by';
setReadOnly( ns, 'filled2dBy', filled2dBy );

/**
* @name filled3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled3d}
*/
import filled3d from '@stdlib/array-base-filled3d';
setReadOnly( ns, 'filled3d', filled3d );

/**
* @name filled3dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled3d-by}
*/
import filled3dBy from '@stdlib/array-base-filled3d-by';
setReadOnly( ns, 'filled3dBy', filled3dBy );

/**
* @name filled4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled4d}
*/
import filled4d from '@stdlib/array-base-filled4d';
setReadOnly( ns, 'filled4d', filled4d );

/**
* @name filled4dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled4d-by}
*/
import filled4dBy from '@stdlib/array-base-filled4d-by';
setReadOnly( ns, 'filled4dBy', filled4dBy );

/**
* @name filled5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled5d}
*/
import filled5d from '@stdlib/array-base-filled5d';
setReadOnly( ns, 'filled5d', filled5d );

/**
* @name filled5dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filled5d-by}
*/
import filled5dBy from '@stdlib/array-base-filled5d-by';
setReadOnly( ns, 'filled5dBy', filled5dBy );

/**
* @name fillednd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fillednd}
*/
import fillednd from '@stdlib/array-base-fillednd';
setReadOnly( ns, 'fillednd', fillednd );

/**
* @name filledndBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fillednd-by}
*/
import filledndBy from '@stdlib/array-base-fillednd-by';
setReadOnly( ns, 'filledndBy', filledndBy );

/**
* @name filter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/filter}
*/
import filter from '@stdlib/array-base-filter';
setReadOnly( ns, 'filter', filter );

/**
* @name first
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/first}
*/
import first from '@stdlib/array-base-first';
setReadOnly( ns, 'first', first );

/**
* @name flatten
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten}
*/
import flatten from '@stdlib/array-base-flatten';
setReadOnly( ns, 'flatten', flatten );

/**
* @name flattenBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten-by}
*/
import flattenBy from '@stdlib/array-base-flatten-by';
setReadOnly( ns, 'flattenBy', flattenBy );

/**
* @name flatten2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten2d}
*/
import flatten2d from '@stdlib/array-base-flatten2d';
setReadOnly( ns, 'flatten2d', flatten2d );

/**
* @name flatten2dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten2d-by}
*/
import flatten2dBy from '@stdlib/array-base-flatten2d-by';
setReadOnly( ns, 'flatten2dBy', flatten2dBy );

/**
* @name flatten3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten3d}
*/
import flatten3d from '@stdlib/array-base-flatten3d';
setReadOnly( ns, 'flatten3d', flatten3d );

/**
* @name flatten3dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten3d-by}
*/
import flatten3dBy from '@stdlib/array-base-flatten3d-by';
setReadOnly( ns, 'flatten3dBy', flatten3dBy );

/**
* @name flatten4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten4d}
*/
import flatten4d from '@stdlib/array-base-flatten4d';
setReadOnly( ns, 'flatten4d', flatten4d );

/**
* @name flatten4dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten4d-by}
*/
import flatten4dBy from '@stdlib/array-base-flatten4d-by';
setReadOnly( ns, 'flatten4dBy', flatten4dBy );

/**
* @name flatten5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten5d}
*/
import flatten5d from '@stdlib/array-base-flatten5d';
setReadOnly( ns, 'flatten5d', flatten5d );

/**
* @name flatten5dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flatten5d-by}
*/
import flatten5dBy from '@stdlib/array-base-flatten5d-by';
setReadOnly( ns, 'flatten5dBy', flatten5dBy );

/**
* @name fliplr2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fliplr2d}
*/
import fliplr2d from '@stdlib/array-base-fliplr2d';
setReadOnly( ns, 'fliplr2d', fliplr2d );

/**
* @name fliplr3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fliplr3d}
*/
import fliplr3d from '@stdlib/array-base-fliplr3d';
setReadOnly( ns, 'fliplr3d', fliplr3d );

/**
* @name fliplr4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fliplr4d}
*/
import fliplr4d from '@stdlib/array-base-fliplr4d';
setReadOnly( ns, 'fliplr4d', fliplr4d );

/**
* @name fliplr5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/fliplr5d}
*/
import fliplr5d from '@stdlib/array-base-fliplr5d';
setReadOnly( ns, 'fliplr5d', fliplr5d );

/**
* @name flipud2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flipud2d}
*/
import flipud2d from '@stdlib/array-base-flipud2d';
setReadOnly( ns, 'flipud2d', flipud2d );

/**
* @name flipud3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flipud3d}
*/
import flipud3d from '@stdlib/array-base-flipud3d';
setReadOnly( ns, 'flipud3d', flipud3d );

/**
* @name flipud4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flipud4d}
*/
import flipud4d from '@stdlib/array-base-flipud4d';
setReadOnly( ns, 'flipud4d', flipud4d );

/**
* @name flipud5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/flipud5d}
*/
import flipud5d from '@stdlib/array-base-flipud5d';
setReadOnly( ns, 'flipud5d', flipud5d );

/**
* @name forEach
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/for-each}
*/
import forEach from '@stdlib/array-base-for-each';
setReadOnly( ns, 'forEach', forEach );

/**
* @name strided2array
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/from-strided}
*/
import strided2array from '@stdlib/array-base-from-strided';
setReadOnly( ns, 'strided2array', strided2array );

/**
* @name getter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/getter}
*/
import getter from '@stdlib/array-base-getter';
setReadOnly( ns, 'getter', getter );

/**
* @name groupEntries
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-entries}
*/
import groupEntries from '@stdlib/array-base-group-entries';
setReadOnly( ns, 'groupEntries', groupEntries );

/**
* @name groupEntriesBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-entries-by}
*/
import groupEntriesBy from '@stdlib/array-base-group-entries-by';
setReadOnly( ns, 'groupEntriesBy', groupEntriesBy );

/**
* @name groupIndices
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-indices}
*/
import groupIndices from '@stdlib/array-base-group-indices';
setReadOnly( ns, 'groupIndices', groupIndices );

/**
* @name groupIndicesBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-indices-by}
*/
import groupIndicesBy from '@stdlib/array-base-group-indices-by';
setReadOnly( ns, 'groupIndicesBy', groupIndicesBy );

/**
* @name groupValues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-values}
*/
import groupValues from '@stdlib/array-base-group-values';
setReadOnly( ns, 'groupValues', groupValues );

/**
* @name groupValuesBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-values-by}
*/
import groupValuesBy from '@stdlib/array-base-group-values-by';
setReadOnly( ns, 'groupValuesBy', groupValuesBy );

/**
* @name groupValuesOnKey
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/group-values-on-key}
*/
import groupValuesOnKey from '@stdlib/array-base-group-values-on-key';
setReadOnly( ns, 'groupValuesOnKey', groupValuesOnKey );

/**
* @name incrspace
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/incrspace}
*/
import incrspace from '@stdlib/array-base-incrspace';
setReadOnly( ns, 'incrspace', incrspace );

/**
* @name indexOf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/index-of}
*/
import indexOf from '@stdlib/array-base-index-of';
setReadOnly( ns, 'indexOf', indexOf );

/**
* @name indexOfSameValue
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/index-of-same-value}
*/
import indexOfSameValue from '@stdlib/array-base-index-of-same-value';
setReadOnly( ns, 'indexOfSameValue', indexOfSameValue );

/**
* @name indicesComplement
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/indices-complement}
*/
import indicesComplement from '@stdlib/array-base-indices-complement';
setReadOnly( ns, 'indicesComplement', indicesComplement );

/**
* @name insertAt
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/insert-at}
*/
import insertAt from '@stdlib/array-base-insert-at';
setReadOnly( ns, 'insertAt', insertAt );

/**
* @name join
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/join}
*/
import join from '@stdlib/array-base-join';
setReadOnly( ns, 'join', join );

/**
* @name last
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/last}
*/
import last from '@stdlib/array-base-last';
setReadOnly( ns, 'last', last );

/**
* @name lastIndexOf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/last-index-of}
*/
import lastIndexOf from '@stdlib/array-base-last-index-of';
setReadOnly( ns, 'lastIndexOf', lastIndexOf );

/**
* @name lastIndexOfSameValue
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/last-index-of-same-value}
*/
import lastIndexOfSameValue from '@stdlib/array-base-last-index-of-same-value';
setReadOnly( ns, 'lastIndexOfSameValue', lastIndexOfSameValue );

/**
* @name linspace
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/linspace}
*/
import linspace from '@stdlib/array-base-linspace';
setReadOnly( ns, 'linspace', linspace );

/**
* @name linspace2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/linspace2d}
*/
import linspace2d from '@stdlib/array-base-linspace2d';
setReadOnly( ns, 'linspace2d', linspace2d );

/**
* @name logspace
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/logspace}
*/
import logspace from '@stdlib/array-base-logspace';
setReadOnly( ns, 'logspace', logspace );

/**
* @name map
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/map}
*/
import map from '@stdlib/array-base-map';
setReadOnly( ns, 'map', map );

/**
* @name map2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/map2d}
*/
import map2d from '@stdlib/array-base-map2d';
setReadOnly( ns, 'map2d', map2d );

/**
* @name map3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/map3d}
*/
import map3d from '@stdlib/array-base-map3d';
setReadOnly( ns, 'map3d', map3d );

/**
* @name map4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/map4d}
*/
import map4d from '@stdlib/array-base-map4d';
setReadOnly( ns, 'map4d', map4d );

/**
* @name map5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/map5d}
*/
import map5d from '@stdlib/array-base-map5d';
setReadOnly( ns, 'map5d', map5d );

/**
* @name minSignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/min-signed-integer-dtype}
*/
import minSignedIntegerDataType from '@stdlib/array-base-min-signed-integer-dtype';
setReadOnly( ns, 'minSignedIntegerDataType', minSignedIntegerDataType );

/**
* @name minUnsignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/min-unsigned-integer-dtype}
*/
import minUnsignedIntegerDataType from '@stdlib/array-base-min-unsigned-integer-dtype';
setReadOnly( ns, 'minUnsignedIntegerDataType', minUnsignedIntegerDataType );

/**
* @name mskbinary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskbinary2d}
*/
import mskbinary2d from '@stdlib/array-base-mskbinary2d';
setReadOnly( ns, 'mskbinary2d', mskbinary2d );

/**
* @name mskbinary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskbinary3d}
*/
import mskbinary3d from '@stdlib/array-base-mskbinary3d';
setReadOnly( ns, 'mskbinary3d', mskbinary3d );

/**
* @name mskbinary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskbinary4d}
*/
import mskbinary4d from '@stdlib/array-base-mskbinary4d';
setReadOnly( ns, 'mskbinary4d', mskbinary4d );

/**
* @name mskbinary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskbinary5d}
*/
import mskbinary5d from '@stdlib/array-base-mskbinary5d';
setReadOnly( ns, 'mskbinary5d', mskbinary5d );

/**
* @name mskfilter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskfilter}
*/
import mskfilter from '@stdlib/array-base-mskfilter';
setReadOnly( ns, 'mskfilter', mskfilter );

/**
* @name mskfilterMap
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskfilter-map}
*/
import mskfilterMap from '@stdlib/array-base-mskfilter-map';
setReadOnly( ns, 'mskfilterMap', mskfilterMap );

/**
* @name mskfilter2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskfilter2}
*/
import mskfilter2 from '@stdlib/array-base-mskfilter2';
setReadOnly( ns, 'mskfilter2', mskfilter2 );

/**
* @name mskfiltern
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskfiltern}
*/
import mskfiltern from '@stdlib/array-base-mskfiltern';
setReadOnly( ns, 'mskfiltern', mskfiltern );

/**
* @name mskput
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskput}
*/
import mskput from '@stdlib/array-base-mskput';
setReadOnly( ns, 'mskput', mskput );

/**
* @name mskreject
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskreject}
*/
import mskreject from '@stdlib/array-base-mskreject';
setReadOnly( ns, 'mskreject', mskreject );

/**
* @name mskrejectMap
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskreject-map}
*/
import mskrejectMap from '@stdlib/array-base-mskreject-map';
setReadOnly( ns, 'mskrejectMap', mskrejectMap );

/**
* @name mskunary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskunary2d}
*/
import mskunary2d from '@stdlib/array-base-mskunary2d';
setReadOnly( ns, 'mskunary2d', mskunary2d );

/**
* @name mskunary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskunary3d}
*/
import mskunary3d from '@stdlib/array-base-mskunary3d';
setReadOnly( ns, 'mskunary3d', mskunary3d );

/**
* @name mskunary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskunary4d}
*/
import mskunary4d from '@stdlib/array-base-mskunary4d';
setReadOnly( ns, 'mskunary4d', mskunary4d );

/**
* @name mskunary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/mskunary5d}
*/
import mskunary5d from '@stdlib/array-base-mskunary5d';
setReadOnly( ns, 'mskunary5d', mskunary5d );

/**
* @name nCartesianProduct
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/n-cartesian-product}
*/
import nCartesianProduct from '@stdlib/array-base-n-cartesian-product';
setReadOnly( ns, 'nCartesianProduct', nCartesianProduct );

/**
* @name nested2objects
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/nested2objects}
*/
import nested2objects from '@stdlib/array-base-nested2objects';
setReadOnly( ns, 'nested2objects', nested2objects );

/**
* @name nested2views
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/nested2views}
*/
import nested2views from '@stdlib/array-base-nested2views';
setReadOnly( ns, 'nested2views', nested2views );

/**
* @name none
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/none}
*/
import none from '@stdlib/array-base-none';
setReadOnly( ns, 'none', none );

/**
* @name noneBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/none-by}
*/
import noneBy from '@stdlib/array-base-none-by';
setReadOnly( ns, 'noneBy', noneBy );

/**
* @name noneByRight
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/none-by-right}
*/
import noneByRight from '@stdlib/array-base-none-by-right';
setReadOnly( ns, 'noneByRight', noneByRight );

/**
* @name nulls
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/nulls}
*/
import nulls from '@stdlib/array-base-nulls';
setReadOnly( ns, 'nulls', nulls );

/**
* @name oneTo
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/one-to}
*/
import oneTo from '@stdlib/array-base-one-to';
setReadOnly( ns, 'oneTo', oneTo );

/**
* @name ones
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ones}
*/
import ones from '@stdlib/array-base-ones';
setReadOnly( ns, 'ones', ones );

/**
* @name ones2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ones2d}
*/
import ones2d from '@stdlib/array-base-ones2d';
setReadOnly( ns, 'ones2d', ones2d );

/**
* @name ones3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ones3d}
*/
import ones3d from '@stdlib/array-base-ones3d';
setReadOnly( ns, 'ones3d', ones3d );

/**
* @name ones4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ones4d}
*/
import ones4d from '@stdlib/array-base-ones4d';
setReadOnly( ns, 'ones4d', ones4d );

/**
* @name ones5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ones5d}
*/
import ones5d from '@stdlib/array-base-ones5d';
setReadOnly( ns, 'ones5d', ones5d );

/**
* @name onesnd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/onesnd}
*/
import onesnd from '@stdlib/array-base-onesnd';
setReadOnly( ns, 'onesnd', onesnd );

/**
* @name place
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/place}
*/
import place from '@stdlib/array-base-place';
setReadOnly( ns, 'place', place );

/**
* @name put
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/put}
*/
import put from '@stdlib/array-base-put';
setReadOnly( ns, 'put', put );

/**
* @name quaternary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quaternary2d}
*/
import quaternary2d from '@stdlib/array-base-quaternary2d';
setReadOnly( ns, 'quaternary2d', quaternary2d );

/**
* @name quaternary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quaternary3d}
*/
import quaternary3d from '@stdlib/array-base-quaternary3d';
setReadOnly( ns, 'quaternary3d', quaternary3d );

/**
* @name quaternary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quaternary4d}
*/
import quaternary4d from '@stdlib/array-base-quaternary4d';
setReadOnly( ns, 'quaternary4d', quaternary4d );

/**
* @name quaternary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quaternary5d}
*/
import quaternary5d from '@stdlib/array-base-quaternary5d';
setReadOnly( ns, 'quaternary5d', quaternary5d );

/**
* @name quinary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quinary2d}
*/
import quinary2d from '@stdlib/array-base-quinary2d';
setReadOnly( ns, 'quinary2d', quinary2d );

/**
* @name quinary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quinary3d}
*/
import quinary3d from '@stdlib/array-base-quinary3d';
setReadOnly( ns, 'quinary3d', quinary3d );

/**
* @name quinary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quinary4d}
*/
import quinary4d from '@stdlib/array-base-quinary4d';
setReadOnly( ns, 'quinary4d', quinary4d );

/**
* @name quinary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/quinary5d}
*/
import quinary5d from '@stdlib/array-base-quinary5d';
setReadOnly( ns, 'quinary5d', quinary5d );

/**
* @name reject
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/reject}
*/
import reject from '@stdlib/array-base-reject';
setReadOnly( ns, 'reject', reject );

/**
* @name rekey
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/rekey}
*/
import rekey from '@stdlib/array-base-rekey';
setReadOnly( ns, 'rekey', rekey );

/**
* @name rekeyViews
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/rekey-views}
*/
import rekeyViews from '@stdlib/array-base-rekey-views';
setReadOnly( ns, 'rekeyViews', rekeyViews );

/**
* @name removeAt
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/remove-at}
*/
import removeAt from '@stdlib/array-base-remove-at';
setReadOnly( ns, 'removeAt', removeAt );

/**
* @name reshape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/reshape}
*/
import reshape from '@stdlib/array-base-reshape';
setReadOnly( ns, 'reshape', reshape );

/**
* @name resolveGetter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/resolve-getter}
*/
import resolveGetter from '@stdlib/array-base-resolve-getter';
setReadOnly( ns, 'resolveGetter', resolveGetter );

/**
* @name resolveSetter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/resolve-setter}
*/
import resolveSetter from '@stdlib/array-base-resolve-setter';
setReadOnly( ns, 'resolveSetter', resolveSetter );

/**
* @name reverse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/reverse}
*/
import reverse from '@stdlib/array-base-reverse';
setReadOnly( ns, 'reverse', reverse );

/**
* @name scatterFilled
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/scatter-filled}
*/
import scatterFilled from '@stdlib/array-base-scatter-filled';
setReadOnly( ns, 'scatterFilled', scatterFilled );

/**
* @name scattered
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/scattered}
*/
import scattered from '@stdlib/array-base-scattered';
setReadOnly( ns, 'scattered', scattered );

/**
* @name setter
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/setter}
*/
import setter from '@stdlib/array-base-setter';
setReadOnly( ns, 'setter', setter );

/**
* @name slice
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/slice}
*/
import slice from '@stdlib/array-base-slice';
setReadOnly( ns, 'slice', slice );

/**
* @name strided2array2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/strided2array2d}
*/
import strided2array2d from '@stdlib/array-base-strided2array2d';
setReadOnly( ns, 'strided2array2d', strided2array2d );

/**
* @name strided2array3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/strided2array3d}
*/
import strided2array3d from '@stdlib/array-base-strided2array3d';
setReadOnly( ns, 'strided2array3d', strided2array3d );

/**
* @name strided2array4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/strided2array4d}
*/
import strided2array4d from '@stdlib/array-base-strided2array4d';
setReadOnly( ns, 'strided2array4d', strided2array4d );

/**
* @name strided2array5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/strided2array5d}
*/
import strided2array5d from '@stdlib/array-base-strided2array5d';
setReadOnly( ns, 'strided2array5d', strided2array5d );

/**
* @name symmetric
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/symmetric}
*/
import symmetric from '@stdlib/array-base-symmetric';
setReadOnly( ns, 'symmetric', symmetric );

/**
* @name symmetricBanded
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/array/base/symmetric-banded}
*/
import symmetricBanded from '@stdlib/array-base-symmetric-banded';
setReadOnly( ns, 'symmetricBanded', symmetricBanded );

/**
* @name take
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take}
*/
import take from '@stdlib/array-base-take';
setReadOnly( ns, 'take', take );

/**
* @name takeIndexed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take-indexed}
*/
import takeIndexed from '@stdlib/array-base-take-indexed';
setReadOnly( ns, 'takeIndexed', takeIndexed );

/**
* @name takeIndexed2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take-indexed2}
*/
import takeIndexed2 from '@stdlib/array-base-take-indexed2';
setReadOnly( ns, 'takeIndexed2', takeIndexed2 );

/**
* @name takeMap
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take-map}
*/
import takeMap from '@stdlib/array-base-take-map';
setReadOnly( ns, 'takeMap', takeMap );

/**
* @name take2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take2d}
*/
import take2d from '@stdlib/array-base-take2d';
setReadOnly( ns, 'take2d', take2d );

/**
* @name take3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/take3d}
*/
import take3d from '@stdlib/array-base-take3d';
setReadOnly( ns, 'take3d', take3d );

/**
* @name ternary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ternary2d}
*/
import ternary2d from '@stdlib/array-base-ternary2d';
setReadOnly( ns, 'ternary2d', ternary2d );

/**
* @name ternary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ternary3d}
*/
import ternary3d from '@stdlib/array-base-ternary3d';
setReadOnly( ns, 'ternary3d', ternary3d );

/**
* @name ternary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ternary4d}
*/
import ternary4d from '@stdlib/array-base-ternary4d';
setReadOnly( ns, 'ternary4d', ternary4d );

/**
* @name ternary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/ternary5d}
*/
import ternary5d from '@stdlib/array-base-ternary5d';
setReadOnly( ns, 'ternary5d', ternary5d );

/**
* @name toAccessorArray
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/to-accessor-array}
*/
import toAccessorArray from '@stdlib/array-base-to-accessor-array';
setReadOnly( ns, 'toAccessorArray', toAccessorArray );

/**
* @name toDeduped
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/to-deduped}
*/
import toDeduped from '@stdlib/array-base-to-deduped';
setReadOnly( ns, 'toDeduped', toDeduped );

/**
* @name toFilled
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/to-filled}
*/
import toFilled from '@stdlib/array-base-to-filled';
setReadOnly( ns, 'toFilled', toFilled );

/**
* @name toInsertedAt
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/to-inserted-at}
*/
import toInsertedAt from '@stdlib/array-base-to-inserted-at';
setReadOnly( ns, 'toInsertedAt', toInsertedAt );

/**
* @name toReversed
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/to-reversed}
*/
import toReversed from '@stdlib/array-base-to-reversed';
setReadOnly( ns, 'toReversed', toReversed );

/**
* @name trues
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/trues}
*/
import trues from '@stdlib/array-base-trues';
setReadOnly( ns, 'trues', trues );

/**
* @name unary2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary2d}
*/
import unary2d from '@stdlib/array-base-unary2d';
setReadOnly( ns, 'unary2d', unary2d );

/**
* @name unary2dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary2d-by}
*/
import unary2dBy from '@stdlib/array-base-unary2d-by';
setReadOnly( ns, 'unary2dBy', unary2dBy );

/**
* @name unary3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary3d}
*/
import unary3d from '@stdlib/array-base-unary3d';
setReadOnly( ns, 'unary3d', unary3d );

/**
* @name unary3dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary3d-by}
*/
import unary3dBy from '@stdlib/array-base-unary3d-by';
setReadOnly( ns, 'unary3dBy', unary3dBy );

/**
* @name unary4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary4d}
*/
import unary4d from '@stdlib/array-base-unary4d';
setReadOnly( ns, 'unary4d', unary4d );

/**
* @name unary4dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary4d-by}
*/
import unary4dBy from '@stdlib/array-base-unary4d-by';
setReadOnly( ns, 'unary4dBy', unary4dBy );

/**
* @name unary5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary5d}
*/
import unary5d from '@stdlib/array-base-unary5d';
setReadOnly( ns, 'unary5d', unary5d );

/**
* @name unary5dBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unary5d-by}
*/
import unary5dBy from '@stdlib/array-base-unary5d-by';
setReadOnly( ns, 'unary5dBy', unary5dBy );

/**
* @name unarynd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unarynd}
*/
import unarynd from '@stdlib/array-base-unarynd';
setReadOnly( ns, 'unarynd', unarynd );

/**
* @name unitspace
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/unitspace}
*/
import unitspace from '@stdlib/array-base-unitspace';
setReadOnly( ns, 'unitspace', unitspace );

/**
* @name where
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/where}
*/
import where from '@stdlib/array-base-where';
setReadOnly( ns, 'where', where );

/**
* @name arrayWith
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/with}
*/
import arrayWith from '@stdlib/array-base-with';
setReadOnly( ns, 'arrayWith', arrayWith );

/**
* @name without
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/without}
*/
import without from '@stdlib/array-base-without';
setReadOnly( ns, 'without', without );

/**
* @name zeroTo
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zero-to}
*/
import zeroTo from '@stdlib/array-base-zero-to';
setReadOnly( ns, 'zeroTo', zeroTo );

/**
* @name zeros
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zeros}
*/
import zeros from '@stdlib/array-base-zeros';
setReadOnly( ns, 'zeros', zeros );

/**
* @name zeros2d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zeros2d}
*/
import zeros2d from '@stdlib/array-base-zeros2d';
setReadOnly( ns, 'zeros2d', zeros2d );

/**
* @name zeros3d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zeros3d}
*/
import zeros3d from '@stdlib/array-base-zeros3d';
setReadOnly( ns, 'zeros3d', zeros3d );

/**
* @name zeros4d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zeros4d}
*/
import zeros4d from '@stdlib/array-base-zeros4d';
setReadOnly( ns, 'zeros4d', zeros4d );

/**
* @name zeros5d
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zeros5d}
*/
import zeros5d from '@stdlib/array-base-zeros5d';
setReadOnly( ns, 'zeros5d', zeros5d );

/**
* @name zerosnd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zerosnd}
*/
import zerosnd from '@stdlib/array-base-zerosnd';
setReadOnly( ns, 'zerosnd', zerosnd );

/**
* @name zip
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zip}
*/
import zip from '@stdlib/array-base-zip';
setReadOnly( ns, 'zip', zip );

/**
* @name zip2object
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zip2object}
*/
import zip2object from '@stdlib/array-base-zip2object';
setReadOnly( ns, 'zip2object', zip2object );

/**
* @name zip2objects
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zip2objects}
*/
import zip2objects from '@stdlib/array-base-zip2objects';
setReadOnly( ns, 'zip2objects', zip2objects );

/**
* @name zip2views
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/array/base/zip2views}
*/
import zip2views from '@stdlib/array-base-zip2views';
setReadOnly( ns, 'zip2views', zip2views );


// EXPORTS //

export default ns;
